import { createClient, Client } from 'graphql-ws';
import { getJWTTokens } from '@shared/libs/local-storage';
import { getWsEndpoint } from '@shared/api/api-url';
import { ensureValidAccessToken } from '@shared/api/ws-auth';
import {
  applyProjects,
  clearFilters,
  FILTERS_UPDATES_SUBSCRIPTION,
  mapFiltersProjectsPayload,
  markFiltersConnecting,
  markFiltersError,
  type FiltersUpdatesPayload,
} from '@shared/api/filters-ws';
import {
  applyClients,
  buildSminexClubClientsByEventSubscription,
  clearSminexClubClients,
  mapSminexClubClientsByEventPayload,
  markClientsConnecting,
  markClientsError,
  type SminexClubClientsByEventPayload,
} from '@shared/api/sminex-club-clients-ws';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;
const MAX_RETRY_ATTEMPTS = 5;

let client: Client | null = null;
let unsubscribeFilters: (() => void) | null = null;
let unsubscribeClients: (() => void) | null = null;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let backoffMs = INITIAL_BACKOFF_MS;
let retryAttemptCount = 0;
let generation = 0;
let activeEventId = 0;
let activeSilent = false;

const clearRetryTimeout = () => {
  if (!retryTimeout) return;
  clearTimeout(retryTimeout);
  retryTimeout = null;
};

const resetRetryState = () => {
  retryAttemptCount = 0;
  backoffMs = INITIAL_BACKOFF_MS;
};

const disposeClient = () => {
  unsubscribeFilters?.();
  unsubscribeFilters = null;
  unsubscribeClients?.();
  unsubscribeClients = null;

  if (!client) return;

  const activeClient = client;
  client = null;
  void Promise.resolve(activeClient.dispose()).catch(() => {});
};

const isUnauthorizedError = (errors: readonly { message?: string }[] | undefined): boolean =>
  !!errors?.some((error) => error.message?.toLowerCase().includes('unauthorized'));

const isUnauthorizedTransportError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const { code, reason, message } = error as { code?: unknown; reason?: unknown; message?: unknown };
  const errorText = typeof reason === 'string' ? reason : typeof message === 'string' ? message : '';

  return code === 4401 || code === 4403 || errorText.toLowerCase().includes('unauthorized');
};

const scheduleRetry = (currentGeneration: number, eventId: number) => {
  clearRetryTimeout();

  if (retryAttemptCount >= MAX_RETRY_ATTEMPTS) {
    if (!activeSilent) {
      markFiltersError();
      markClientsError(eventId);
    }
    return;
  }

  retryAttemptCount += 1;

  retryTimeout = setTimeout(() => {
    retryTimeout = null;
    if (currentGeneration !== generation) return;
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    void connect(currentGeneration, eventId, activeSilent);
  }, backoffMs);
};

const tryDisposeWhenReady = (receivedFilters: boolean, receivedClients: boolean) => {
  if (receivedFilters && receivedClients) {
    resetRetryState();
    disposeClient();
  }
};

const connect = async (currentGeneration: number, eventId: number, silent = false) => {
  if (currentGeneration !== generation) return;
  if (!eventId) return;

  activeSilent = silent;
  clearRetryTimeout();
  disposeClient();

  if (!silent) {
    markFiltersConnecting();
    markClientsConnecting(eventId);
  }

  const accessToken = await ensureValidAccessToken();
  if (currentGeneration !== generation) return;

  if (!accessToken) {
    if (!silent) {
      markFiltersError();
      markClientsError(eventId);
    }
    return;
  }

  const wsUrl = getWsEndpoint();
  let receivedFilters = false;
  let receivedClients = false;

  try {
    client = createClient({
      url: wsUrl,
      lazy: false,
      retryAttempts: 0,
      connectionParams: () => {
        const { accessToken: token } = getJWTTokens();
        return {
          Authorization: `Bearer ${token}`,
        };
      },
    });

    unsubscribeFilters = client.subscribe(
      { query: FILTERS_UPDATES_SUBSCRIPTION },
      {
        next: (result) => {
          if (currentGeneration !== generation) return;

          if (result.errors?.length) {
            disposeClient();
            if (isUnauthorizedError(result.errors)) {
              if (!silent) {
                markFiltersError();
                markClientsError(eventId);
              }
              return;
            }
            scheduleRetry(currentGeneration, eventId);
            return;
          }

          const mapped = mapFiltersProjectsPayload(result.data as FiltersUpdatesPayload | undefined);
          if (!mapped) return;

          receivedFilters = true;
          applyProjects(mapped);
          tryDisposeWhenReady(receivedFilters, receivedClients);
        },
        error: (error) => {
          if (currentGeneration !== generation || receivedFilters) return;
          disposeClient();
          if (isUnauthorizedTransportError(error)) {
            if (!silent) {
              markFiltersError();
              markClientsError(eventId);
            }
            return;
          }
          scheduleRetry(currentGeneration, eventId);
        },
        complete: () => {
          if (currentGeneration !== generation || receivedFilters) return;
          disposeClient();
          scheduleRetry(currentGeneration, eventId);
        },
      }
    );

    unsubscribeClients = client.subscribe(
      { query: buildSminexClubClientsByEventSubscription(eventId) },
      {
        next: (result) => {
          if (currentGeneration !== generation) return;

          if (result.errors?.length) {
            disposeClient();
            if (isUnauthorizedError(result.errors)) {
              if (!silent) {
                markFiltersError();
                markClientsError(eventId);
              }
              return;
            }
            scheduleRetry(currentGeneration, eventId);
            return;
          }

          const mapped = mapSminexClubClientsByEventPayload(result.data as SminexClubClientsByEventPayload | undefined);
          if (!mapped) return;

          receivedClients = true;
          applyClients(mapped, eventId);
          tryDisposeWhenReady(receivedFilters, receivedClients);
        },
        error: (error) => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          if (isUnauthorizedTransportError(error)) {
            if (!silent) {
              markFiltersError();
              markClientsError(eventId);
            }
            return;
          }
          scheduleRetry(currentGeneration, eventId);
        },
        complete: () => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          scheduleRetry(currentGeneration, eventId);
        },
      }
    );
  } catch {
    disposeClient();
    scheduleRetry(currentGeneration, eventId);
  }
};

export const clearEventPageWs = () => {
  generation += 1;
  clearRetryTimeout();
  disposeClient();
  activeEventId = 0;
  resetRetryState();
  clearFilters();
  clearSminexClubClients();
};

export const ensureEventPageWsLoaded = (options: { eventId: number; force?: boolean; silent?: boolean }) => {
  const { eventId, force = false, silent = false } = options;

  if (!eventId) return;

  if (silent) {
    generation += 1;
    clearRetryTimeout();
    disposeClient();
    activeEventId = eventId;
    resetRetryState();
    const currentGeneration = generation;
    void connect(currentGeneration, eventId, true);
    return;
  }

  if (force || activeEventId !== eventId) {
    generation += 1;
    clearRetryTimeout();
    disposeClient();
    activeEventId = eventId;
    resetRetryState();
    clearFilters();
    clearSminexClubClients();
  } else if (!force) {
    return;
  }

  const currentGeneration = generation;
  void connect(currentGeneration, eventId, false);
};

export const refetchEventPageWs = (eventId: number, options?: { silent?: boolean }) => {
  ensureEventPageWsLoaded({ eventId, force: true, silent: options?.silent });
};
